import { cn } from '@workspace/ui/lib/utils';

import type {
  ProductFilterChoice,
  ProductPropertiesValue,
} from '~/types/Product/interface';

export const getChoiceImage = (choice: ProductFilterChoice) =>
  choice.image?.thumb || choice.image?.url || '';

/** Whether a variant property should render as an image-swatch grid. */
export const hasSwatchImages = (filter: ProductPropertiesValue) =>
  Boolean(filter.containsImage) && filter.choices.some(getChoiceImage);

type Props = {
  filter: ProductPropertiesValue;
  /** Shared radio-group name; also prefixes the DOM ids so equal values across properties stay unique. */
  inputName: string;
  handleChangeOffer: (sku: string) => () => void;
};

/**
 * Image swatches of a variant property: 6 per row, square tiles; a choice
 * without an image falls back to its text label inside the same tile.
 * Out-of-stock tiles are dimmed, values unreachable with the current
 * selection get a dashed border — both stay clickable.
 */
export const VariantSwatchGrid = ({
  filter,
  inputName,
  handleChangeOffer,
}: Props) => (
  <div className="grid w-full max-w-[356px] grid-cols-6 gap-1">
    {filter.choices.map((choice) => {
      const image = getChoiceImage(choice);
      const unreachable = choice.combinationExists === false;
      const outOfStock = !unreachable && !choice.sellable;
      const muted = unreachable || outOfStock;

      return (
        <label
          key={choice.value}
          data-choice-value={choice.value}
          title={choice.label}
          className={cn(
            'relative box-border flex aspect-square cursor-pointer items-center justify-center overflow-hidden desktop:rounded-xl rounded-[10px] border border-stroke-primary border-solid bg-transparent',
            unreachable && 'border-dashed',
            outOfStock && 'bg-surface-transparent-2',
            choice.selected && 'border-2 border-stroke-brand border-solid',
          )}
        >
          <input
            data-radio={choice.label}
            type="radio"
            name={inputName}
            id={`${inputName}_${choice.value}`}
            value={String(choice.value)}
            checked={Boolean(choice.selected)}
            className="hidden"
            onChange={() => {
              if (choice.sku) {
                handleChangeOffer(choice.sku)();
              }
            }}
          />
          {image ? (
            <img
              src={image}
              alt={choice.label}
              loading="lazy"
              className={cn('size-full object-cover', muted && 'opacity-40')}
            />
          ) : (
            <span
              className={cn(
                'overflow-hidden px-0.5 text-center [font:var(--text-100-medium)]',
                choice.selected ? 'text-text-brand' : 'text-text-primary',
                muted && 'text-text-disabled',
              )}
            >
              {choice.label}
            </span>
          )}
        </label>
      );
    })}
  </div>
);
